import { Interpolation, Theme, css } from '@emotion/react'
import { useState } from 'react'

import FormLabel from '../FormLabel'
import Dropdown, { DropdownProps, DropdownMode } from '../Dropdown'
import { LabelVisibility } from '../../../../types'
import { captionTextCss } from '../../../../lib/styles/typography'
import { errorTextColor } from '../../../../lib/styles/colors'

const selectContainerCss = css`
  position: relative;
  padding-bottom: 5px;
  margin-left: 8px;
  margin-right: 8px;
`

const helperTextCss = css`
  ${captionTextCss}
  margin-top: 6px;
  padding-left: 2px;
`

const helperTextErrorCss = css`
  color: ${errorTextColor};
`

export type SelectProps = {
  /** Optional aria-labelledby IDs */
  ariaLabelledBy?: string
  /** Custom class name for the select container */
  className?: string
  /** Custom CSS styles to apply to the select container */
  css?: Interpolation<Theme>
  /** Indicates field is an error state */
  error?: boolean
  /** Id of control */
  id: string
  /** Optional helper text to display with the dropdown */
  helperText?: any
  /** Label for field */
  label: string
  /** Label key for an optionl */
  labelKey?: string
  /** Label visibility behavior */
  labelVisibility?: LabelVisibility
  /** The type of dropdown, such as Simple or Filter or Multiselect */
  mode?: DropdownMode
  /** Optional callback for dropdown blur */
  onDropdownBlur?: () => void
  /** Optional callback for dropdown focus */
  onDropdownFocus?: () => void
  /** Whether to show the input field */
  showInput?: boolean
  /** Indicates that label should be screen-ready only */
  srOnlyLabel?: boolean
  /** Value key for an option */
  valueKey?: string
  /** Key down handler */
  onKeyDown?: (e: any) => void
} & DropdownProps

const Select = ({
  ariaLabelledBy: externalAriaLabelledBy,
  error = false,
  id,
  className,
  css: customCss,
  helperText,
  label,
  labelKey = 'label',
  labelVisibility = LabelVisibility.DEFAULT,
  mode = DropdownMode.Filter,
  onDropdownBlur,
  onDropdownFocus,
  onKeyDown,
  placeholder,
  showInput,
  srOnlyLabel = false,
  value,
  valueKey = 'value',
  ...props
}: SelectProps) => {
  const [focused, setFocused] = useState(false)

  const handleFocus = () => {
    setFocused(true)
    if (onDropdownFocus) {
      onDropdownFocus()
    }
  }

  const handleBlur = () => {
    setFocused(false)
    if (onDropdownBlur) {
      onDropdownBlur()
    }
  }

  const labelHiddenOnFilled =
    labelVisibility === LabelVisibility.VISIBLE_ON_FOCUS && value && !focused
  const labelHidden = labelVisibility === LabelVisibility.HIDDEN

  const helperTextSetsCss = [helperTextCss, error && helperTextErrorCss]

  // Build aria-labelledby chain: FormLabel ID + any external IDs
  // FormLabel must come first (primary label), then any external IDs
  const fullAriaLabelledBy = externalAriaLabelledBy
    ? `${id}-label ${externalAriaLabelledBy}`
    : `${id}-label`

  return (
    <div css={[selectContainerCss, customCss]} className={className}>
      <FormLabel
        associatedId={id}
        error={error}
        focused={focused}
        shrink={!!value || focused}
        srOnly={labelHidden || labelHiddenOnFilled || srOnlyLabel || !!(!focused && placeholder)}
      >
        {label}
      </FormLabel>
      <Dropdown
        id={id}
        {...props}
        ariaLabelledBy={fullAriaLabelledBy}
        error={error}
        label={label}
        labelKey={labelKey}
        mode={mode}
        onBlur={handleBlur}
        onFocus={handleFocus}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        showInput={showInput}
        srOnlyLabel={srOnlyLabel}
        value={value}
        valueKey={valueKey}
      />
      {helperText && typeof helperText === 'string' ? (
        <p aria-live={error ? 'polite' : 'off'} css={helperTextSetsCss}>
          {helperText}
        </p>
      ) : (
        helperText
      )}
    </div>
  )
}

export default Select
